import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, Instagram, Youtube, MapPin, Send } from 'lucide-react';
import Logo from './Logo';
import { toast } from 'sonner';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required.');
      return;
    }
    toast.success('Your request has reached the atelier. We respond within 24 hours.');
    setForm({ name: '', email: '', phone: '', message: '' });
  };
  return (
    <section id="contact" className="py-28 bg-[#faf7f0] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="ornament mb-4">
            <span className="section-eyebrow">Begin the Conversation</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#1a1714]">
            Book your <span className="italic font-cormorant gold-text">private reading</span>
          </h2>
          <p className="mt-5 text-[#4a4035] text-[15px] font-light leading-relaxed max-w-md">
            Sessions are conducted online and in-person across Delhi / NCR. Each reading is unhurried, confidential and crafted around your specific question.
          </p>
          <div className="mt-10 space-y-5">
            {[
              { icon: Phone, label: '+91 • 98107 33890', sub: 'Mon – Sat, 10am – 7pm IST' },
              { icon: Mail, label: 'studio@destinydigits.in', sub: 'For consultations & press' },
              { icon: MapPin, label: 'Delhi / NCR, India', sub: 'By appointment only' },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="flex items-center gap-4">
                  <div className="w-11 h-11 border border-[#c9a961]/50 flex items-center justify-center rotate-45">
                    <Icon size={16} className="-rotate-45 text-[#a8842c]" />
                  </div>
                  <div>
                    <div className="font-display text-lg text-[#1a1714]">{c.label}</div>
                    <div className="text-[11px] tracking-[0.2em] uppercase text-[#6b5e4f]">{c.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-10 flex gap-3">
            {[
              { Ic: Instagram, name: 'instagram' },
              { Ic: Youtube, name: 'youtube' },
              { Ic: MessageCircle, name: 'whatsapp' },
            ].map(({ Ic, name }) => (
              <a key={name} href="#" aria-label={name} className="w-11 h-11 border border-[#c9a961]/40 flex items-center justify-center text-[#a8842c] hover:bg-[#1a1714] hover:text-[#e8d9a8] transition-all">
                <Ic size={16} />
              </a>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 card-luxe p-8 md:p-10 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="section-eyebrow block mb-2">Full Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-[#c9a961]/50 py-2 focus:outline-none focus:border-[#a8842c] text-[#1a1714]" />
            </div>
            <div>
              <label className="section-eyebrow block mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-[#c9a961]/50 py-2 focus:outline-none focus:border-[#a8842c] text-[#1a1714]" />
            </div>
            <div>
              <label className="section-eyebrow block mb-2">Phone (Optional)</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-transparent border-b border-[#c9a961]/50 py-2 focus:outline-none focus:border-[#a8842c] text-[#1a1714]" />
            </div>
            <div>
              <label className="section-eyebrow block mb-2">Service of Interest</label>
              <select className="w-full bg-transparent border-b border-[#c9a961]/50 py-2 focus:outline-none focus:border-[#a8842c] text-[#1a1714]">
                <option>Numerology Consultation</option>
                <option>Career Counselling</option>
                <option>Baby Name Analysis</option>
                <option>Matrimonial Reading</option>
                <option>Business Consultation</option>
              </select>
            </div>
          </div>
          <div>
            <label className="section-eyebrow block mb-2">Your Question / Intent</label>
            <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b border-[#c9a961]/50 py-2 focus:outline-none focus:border-[#a8842c] text-[#1a1714] resize-none" />
          </div>
          <button type="submit" className="btn-gold">
            <Send size={14} /> Send Inquiry
          </button>
        </form>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#1a1714] text-[#a99c84] pt-20 pb-10 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#c9a961] to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Logo variant="light" />
            <p className="mt-6 text-[14px] leading-relaxed text-[#a99c84] max-w-sm font-light">
              A quiet, classy numerology atelier helping souls own their destiny before it owns them. By Mrs. Pujah Chopraa.
            </p>
            <div className="font-cormorant italic text-2xl gold-text mt-6">
              “Own your destiny before it owns you.”
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9a961] mb-5">Atelier</div>
            <ul className="space-y-3 text-[13px]">
              {[
                { l: 'Services', h: '#services' },
                { l: 'Calculators', h: '#calculators' },
                { l: 'Book a Reading', h: '#reading' },
                { l: 'Testimonials', h: '#testimonials' },
              ].map(({ l, h }) => (
                <li key={l}><a href={h} className="hover:text-[#e8d9a8] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9a961] mb-5">Stay in the Aura</div>
            <p className="text-[13px] text-[#a99c84] mb-4 font-light">Quarterly letters on cycles, remedies & rituals. No noise.</p>
            <form onSubmit={(e) => { e.preventDefault(); toast.success('Subscribed to the Destiny Digits letter.'); }} className="flex border border-[#c9a961]/40">
              <input placeholder="your@email.com" className="flex-1 bg-transparent px-3 py-2.5 text-[13px] focus:outline-none text-[#faf7f0] placeholder:text-[#a99c84]" />
              <button className="bg-[#c9a961] text-[#1a1714] px-4 hover:bg-[#e8d9a8] transition-colors">
                <Send size={14} />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[#c9a961]/15 flex flex-col md:flex-row justify-between gap-4 text-[11px] tracking-[0.2em] uppercase text-[#6b5e4f]">
          <div>© {new Date().getFullYear()} Destiny Digits • All Rights Reserved</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-[#c9a961]">Privacy</a>
            <a href="#" className="hover:text-[#c9a961]">Terms</a>
            <a href="#" className="hover:text-[#c9a961]">Refunds</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Contact };
export default Footer;
